import { hasAnyResourceAccess, satisfiesAction } from './actionLevels';
import type {
  NormalizedResourcePermission,
  PermissionAction,
  PermissionContext,
  PermissionScopeType,
  ResourcePermissionApiGrant,
  ResourcePermissionApiRow,
} from './types';

/**
 * Assumptions (adjust when backend confirms):
 * - Empty scopeIds = wildcard for that scopeType (all instances).
 * - API returns effective resource rows; no client-side ltree walk.
 * - Duplicate resourceId rows in API are merged (union scopeIds + actions).
 * - Unknown scopeType uses context.scopeId if ever added to PermissionContext.
 * - Store-scoped rows may list customerId in scopeIds (tenant-wide store access); see matchesScope.
 * - Action checks use privilege levels (see actionLevels.ts); held edit/create implies view, not vice versa.
 */

/** Customer grant on a narrower resource scope covers all instances at that level. */
function isTenantWideGrantForRow(
  row: NormalizedResourcePermission,
  grant: ResourcePermissionApiGrant
): boolean {
  return grant.scopeType === 'customer' && row.scopeType !== 'customer';
}

/** Store-scoped grant where scopeIds lists the customer id (all stores for that customer). */
export function isCustomerWideStoreGrant(
  row: NormalizedResourcePermission,
  context: PermissionContext
): boolean {
  const customerId = context.customerId;
  if (!customerId || row.scopeType !== 'store') {
    return false;
  }
  if (row.grants.length > 0) {
    return row.grants.some(
      (grant) => grant.scopeType === 'customer' && grant.scopeId === customerId
    );
  }
  return row.scopeIds.includes(customerId);
}

export function mergeResourcePermissionRows(
  rows: ResourcePermissionApiRow[]
): NormalizedResourcePermission[] {
  const map = new Map<string, NormalizedResourcePermission>();

  rows.forEach((row) => {
    if (!row?.resourceId) return;
    const existing = map.get(row.resourceId);
    const normalizedGrants = (row.grants ?? []).filter(
      (grant): grant is ResourcePermissionApiGrant =>
        Boolean(grant?.action && grant?.scopeType && grant?.scopeId)
    );

    const grantsByKey = new Map<string, ResourcePermissionApiGrant>();
    [...(existing?.grants ?? []), ...normalizedGrants].forEach((grant) => {
      const key = `${grant.action}|${grant.scopeType}|${grant.scopeId}`;
      if (!grantsByKey.has(key)) {
        grantsByKey.set(key, grant);
      }
    });
    const grants = Array.from(grantsByKey.values());
    const actions = Array.from(new Set(grants.map((grant) => grant.action))) as PermissionAction[];
    const scopeType = row.resourceScopeType || existing?.scopeType || 'customer';
    const scopeIds = Array.from(
      new Set(
        grants.flatMap((grant) => {
          if (grant.scopeType === scopeType) {
            return [grant.scopeId];
          }
          if (grant.scopeType === 'customer' && scopeType !== 'customer') {
            return [grant.scopeId];
          }
          return [];
        })
      )
    );

    map.set(row.resourceId, {
      resourceId: row.resourceId,
      label: row.label || existing?.label || row.resourceId,
      scopeType,
      scopeIds,
      actions,
      grants,
    });
  });

  return Array.from(map.values());
}

function grantMatchesContext(
  row: NormalizedResourcePermission,
  grant: ResourcePermissionApiGrant,
  context: PermissionContext
): boolean {
  // For project/store resources, customer grant means all instances under this customer.
  if (
    grant.scopeType === 'customer' &&
    row.scopeType !== 'customer' &&
    context.customerId
  ) {
    return grant.scopeId === context.customerId;
  }

  const instanceId = resolveScopeInstanceId(grant.scopeType, context);
  if (instanceId == null) {
    return false;
  }
  return grant.scopeId === instanceId;
}

export function buildResourceIndex(
  rows: NormalizedResourcePermission[]
): Map<string, NormalizedResourcePermission> {
  return new Map(rows.map((row) => [row.resourceId, row]));
}

function resolveScopeInstanceId(
  scopeType: PermissionScopeType,
  context: PermissionContext
): string | undefined {
  switch (scopeType) {
    case 'store':
      return context.storeId;
    case 'project':
      return context.projectId;
    case 'customer':
      return context.customerId;
    default:
      return undefined;
  }
}

export function matchesScope(
  row: NormalizedResourcePermission,
  context: PermissionContext
): boolean {
  if (!row.scopeIds.length) {
    return true;
  }

  if (isCustomerWideStoreGrant(row, context)) {
    return true;
  }

  const instanceId = resolveScopeInstanceId(row.scopeType, context);
  if (instanceId == null) {
    return false;
  }

  return row.scopeIds.includes(instanceId);
}

export interface CanOptions {
  /** When false, returns failClosed (default true) */
  ready?: boolean;
  failClosed?: boolean;
}

export function can(
  index: Map<string, NormalizedResourcePermission>,
  resourceId: string,
  action: PermissionAction,
  context: PermissionContext = {},
  options: CanOptions = {}
): boolean {
  const { ready = true, failClosed = true } = options;

  if (!ready) {
    return !failClosed;
  }

  const row = index.get(resourceId);
  if (!row) {
    return false;
  }

  if (row.grants.length > 0) {
    return row.grants.some(
      (grant) =>
        satisfiesAction([grant.action], action) && grantMatchesContext(row, grant, context)
    );
  }

  if (!satisfiesAction(row.actions, action)) {
    return false;
  }

  return matchesScope(row, context);
}

export function canAnyAction(
  index: Map<string, NormalizedResourcePermission>,
  resourceId: string,
  context: PermissionContext = {},
  options: CanOptions = {}
): boolean {
  const { ready = true, failClosed = true } = options;

  if (!ready) {
    return !failClosed;
  }

  const row = index.get(resourceId);
  if (!row) {
    return false;
  }

  if (row.grants.length > 0) {
    return row.grants.some((grant) => grantMatchesContext(row, grant, context));
  }

  if (!hasAnyResourceAccess(row.actions)) {
    return false;
  }

  return matchesScope(row, context);
}

export function getAllowedScopeIds(
  rows: NormalizedResourcePermission[],
  options: {
    resourceIdPrefix?: string;
    scopeType?: PermissionScopeType;
  } = {}
): string[] {
  const { resourceIdPrefix, scopeType } = options;
  const ids = new Set<string>();

  rows.forEach((row) => {
    if (resourceIdPrefix && !row.resourceId.startsWith(resourceIdPrefix)) {
      return;
    }
    if (row.grants.length > 0) {
      row.grants.forEach((grant) => {
        if (scopeType && grant.scopeType !== scopeType) {
          if (scopeType === row.scopeType && isTenantWideGrantForRow(row, grant)) {
            ids.add(grant.scopeId);
          }
          return;
        }
        ids.add(grant.scopeId);
      });
      return;
    }

    if (scopeType && row.scopeType !== scopeType) {
      return;
    }
    row.scopeIds.forEach((id) => ids.add(id));
  });

  return Array.from(ids);
}

/** True if any matching row has empty scopeIds (wildcard at its scopeType) */
export function hasWildcardScope(
  rows: NormalizedResourcePermission[],
  options: {
    resourceIdPrefix?: string;
    scopeType?: PermissionScopeType;
  } = {}
): boolean {
  const { resourceIdPrefix, scopeType } = options;
  return rows.some((row) => {
    if (resourceIdPrefix && !row.resourceId.startsWith(resourceIdPrefix)) {
      return false;
    }
    if (row.grants.length > 0) {
      if (scopeType && row.scopeType === scopeType) {
        if (row.grants.some((grant) => isTenantWideGrantForRow(row, grant))) {
          return true;
        }
      }
      const grants = scopeType
        ? row.grants.filter((grant) => grant.scopeType === scopeType)
        : row.grants;
      if (grants.length === 0) {
        return false;
      }
      return grants.some((grant) => isTenantWideGrantForRow(row, grant));
    }
    if (scopeType && row.scopeType !== scopeType) return false;
    return row.scopeIds.length === 0;
  });
}

export function filterStoreIds(
  storeIds: string[],
  rows: NormalizedResourcePermission[],
  context: PermissionContext,
  resourceIdPrefix?: string
): string[] {
  if (hasWildcardScope(rows, { resourceIdPrefix, scopeType: 'store' })) {
    return storeIds;
  }

  const allowed = new Set(
    getAllowedScopeIds(rows, { resourceIdPrefix, scopeType: 'store' })
  );

  const scopedRows = resourceIdPrefix
    ? rows.filter((r) => r.resourceId.startsWith(resourceIdPrefix))
    : rows;
  const hasStoreScopedRows = scopedRows.some((r) => r.scopeType === 'store');

  if (!hasStoreScopedRows) {
    return storeIds;
  }

  if (allowed.size === 0) {
    return [];
  }

  if (context.customerId && allowed.has(context.customerId)) {
    return storeIds;
  }

  return storeIds.filter((id) => allowed.has(id));
}
